import { action, makeObservable, observable } from 'mobx';

import { getRequest } from '../../services/api';
import type { CoinType } from '../cointype/CoinTypeStore';
import type { RootStore } from '../RootStore';

export interface PlayingNowCounts {
  GC?: number;
  SC?: number;
}

/** No `v1/` prefix: the axios request interceptor adds it. */
export const PLAYING_NOW_PATH = 'games/playing-now';

export const PLAYING_NOW_POLL_MS = 60_000;

/** Well under the poll period, so a stalled request never stacks. */
export const PLAYING_NOW_REQUEST_TIMEOUT_MS = 10_000;

/** Counts older than this are dropped on the next failed fetch, so an outage never freezes badges. */
export const PLAYING_NOW_STALE_MS = 180_000;

/** Empty answers in a row before the strip is released, so a few enabled games cannot flap it every poll. */
export const PLAYING_NOW_EMPTY_RELEASE_COUNT = 3;

interface PlayingNowEnvelope {
  data?: { playingNow?: Record<string, PlayingNowCounts> } | null;
  errors?: unknown[];
}

const COINS: readonly CoinType[] = ['GC', 'SC'];

/** Stricter than `Number(key) > 0`, which accepts `'1e3'`, `' 7'` and `'12.0'`. */
const GAME_ID_KEY = /^[1-9]\d*$/;

const isCount = (value: unknown): value is number => typeof value === 'number' && Number.isInteger(value) && value >= 0;

const isHidden = (): boolean => typeof document !== 'undefined' && document.hidden;

/** `null` means no valid answer (keep current counts); an empty map is a real answer. Bad entries are dropped one by one. */
export function parsePlayingNowResponse(response: unknown): Map<number, PlayingNowCounts> | null {
  const playingNow = (response as PlayingNowEnvelope | null | undefined)?.data?.playingNow;

  if (!playingNow || typeof playingNow !== 'object' || Array.isArray(playingNow)) {
    return null;
  }

  const parsed = new Map<number, PlayingNowCounts>();

  for (const [key, raw] of Object.entries(playingNow)) {
    const gameId = Number(key);

    if (!GAME_ID_KEY.test(key) || !Number.isSafeInteger(gameId) || !raw || typeof raw !== 'object') {
      continue;
    }

    const counts: PlayingNowCounts = {};

    for (const coin of COINS) {
      const value: unknown = (raw as Record<string, unknown>)[coin];

      if (isCount(value)) {
        counts[coin] = value;
      }
    }

    if (counts.GC !== undefined || counts.SC !== undefined) {
      parsed.set(gameId, counts);
    }
  }

  return parsed;
}

/**
 * Live "X playing" counts keyed by game. Polled while the lobby is mounted; the server decides which games
 * carry a count and `usePlayingNowPolling` gates on the LD flag.
 */
export class PlayingNowStore {
  // Kept for parity with the other stores; this one reads nothing from the root.
  private readonly rootStore: RootStore;

  /** Shallow: entries are replaced wholesale, never mutated, so deep observation buys nothing. */
  @observable.shallow
  private counts = new Map<number, PlayingNowCounts>();

  /** Set by any non-empty answer, released after {@link PLAYING_NOW_EMPTY_RELEASE_COUNT} empty ones in a row. */
  @observable
  public stripReserved = false;

  private active = false;

  /** Defers teardown one macrotask so a StrictMode remount (cleanup, then mount) cancels it. */
  private pendingTeardown: ReturnType<typeof setTimeout> | null = null;

  private timer: ReturnType<typeof setInterval> | null = null;

  private inFlight = false;

  /** `Date.now()` of the last parsed answer; `null` before the first one or after `reset()`. */
  private lastSuccessAt: number | null = null;

  /** Empty answers since the last non-empty one; failed or malformed answers leave it alone. */
  private emptyStreak = 0;

  /** Bumped by `reset()` so an in-flight reply cannot repopulate cleared counts. */
  private generation = 0;

  /** Skipped while hidden or tearing down, so a tick after a flag-off `reset()` cannot refill the badges. */
  private readonly tick = (): void => {
    if (!isHidden() && !this.pendingTeardown) {
      void this.fetch();
    }
  };

  private readonly onVisibilityChange = (): void => {
    if (this.active && !this.pendingTeardown && !isHidden()) {
      void this.fetch();
    }
  };

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this);
  }

  public countFor(masterCasinoGameId: number, coin: CoinType): number | undefined {
    return this.counts.get(masterCasinoGameId)?.[coin];
  }

  /** Fetch now, then every {@link PLAYING_NOW_POLL_MS}. Idempotent; a StrictMode remount cancels the pending teardown. */
  public activate(): void {
    if (this.pendingTeardown) {
      clearTimeout(this.pendingTeardown);
      this.pendingTeardown = null;

      return;
    }

    if (this.active) {
      return;
    }

    this.active = true;
    void this.fetch();
    this.timer = setInterval(this.tick, PLAYING_NOW_POLL_MS);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  /** Counts are kept so the next lobby enter paints the last known badges until its answer replaces them. */
  public deactivate(): void {
    if (!this.active || this.pendingTeardown) {
      return;
    }

    this.pendingTeardown = setTimeout(() => {
      this.pendingTeardown = null;
      this.active = false;
      document.removeEventListener('visibilitychange', this.onVisibilityChange);

      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    }, 0);
  }

  /** Stale counts stay on screen until this request answers (no blink); only a failure drops them. */
  public async fetch(): Promise<void> {
    if (this.inFlight) {
      return;
    }

    const generation = this.generation;
    const controller = new AbortController();
    let timeout: ReturnType<typeof setTimeout> | undefined;
    // Rejects even if the request ignores the abort (e.g. stuck in an async interceptor), so `inFlight` is always released.
    const timedOut = new Promise<never>((_resolve, reject) => {
      timeout = setTimeout(() => {
        controller.abort();
        reject(new Error('playing-now request timed out'));
      }, PLAYING_NOW_REQUEST_TIMEOUT_MS);
    });

    this.inFlight = true;

    try {
      const response = await Promise.race([
        getRequest<PlayingNowEnvelope>(PLAYING_NOW_PATH, undefined, controller.signal, { skipAuthLogout: true }),
        timedOut,
      ]);

      if (generation !== this.generation) {
        return;
      }

      const parsed = parsePlayingNowResponse(response);

      if (parsed) {
        this.lastSuccessAt = Date.now();
        this.apply(parsed);
      } else {
        this.dropIfStale();
      }
    } catch {
      // 503, timeout or network: keep current counts unless they are stale.
      if (generation === this.generation) {
        this.dropIfStale();
      }
    } finally {
      clearTimeout(timeout);

      // `reset()` already released the gate for a newer generation.
      if (generation === this.generation) {
        this.inFlight = false;
      }
    }
  }

  /** Logout or flag off: drop counts and the strip, and ignore any in-flight reply. Polling is unaffected. */
  @action
  public reset(): void {
    this.generation += 1;
    this.inFlight = false;
    this.lastSuccessAt = null;
    this.emptyStreak = 0;
    this.counts.clear();
    this.stripReserved = false;
  }

  /** The strip stays reserved, so dropping counts never shifts a row. */
  @action
  private dropIfStale(): void {
    if (
      this.counts.size > 0 &&
      (this.lastSuccessAt === null || Date.now() - this.lastSuccessAt > PLAYING_NOW_STALE_MS)
    ) {
      this.counts.clear();
    }
  }

  @action
  private apply(next: Map<number, PlayingNowCounts>): void {
    for (const [gameId, incoming] of next) {
      const current = this.counts.get(gameId);

      // Shallow map notifies on reference alone; compare first to avoid re-rendering unchanged tiles.
      if (!current || current.GC !== incoming.GC || current.SC !== incoming.SC) {
        this.counts.set(gameId, incoming);
      }
    }

    for (const gameId of [...this.counts.keys()]) {
      if (!next.has(gameId)) {
        this.counts.delete(gameId);
      }
    }

    // A run of empty answers (kill switch) must not leave a blank strip on every tile.
    if (next.size > 0) {
      this.emptyStreak = 0;
      this.stripReserved = true;
    } else {
      this.emptyStreak += 1;

      if (this.emptyStreak >= PLAYING_NOW_EMPTY_RELEASE_COUNT) {
        this.stripReserved = false;
      }
    }
  }
}
